// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button"
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import NumberedHeading from "../../../../../../shared-components/forms/NumberedHeading";
import ImageAttribution from '../../shared/ImageAttribution';
import WYSIWYGEditor from "../../../../../../shared-components/WYSIWYGEditor";
// data
import { ReviewData, MediaObject } from "../../../data/Defaults";

export default function IntroImageAndTextSlideArray({ control, register, setValue, getValues, errors, inputDir }) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.core'
  })

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <Accordion defaultExpanded className='bg-transparent shadow-none'>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`${index}`} id={`${index}`} className="py-0 px-16 m-0 rounded bg-white shadow-sm" sx={{border: 'solid 1px', borderColor: '#bdbdbd'}}>
                    <Typography className="m-0 font-semibold">
                      Slide {index + 1}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box className="p-0">
                      <NumberedHeading number="1" title="Image" instructions="Add image attribution." />
                      <Controller 
                        control={control}
                        name={`content.core.${index}.media`}
                        render={({ field }) => <ImageAttribution targetObj={`content.core.${index}.media`} errors={errors.content?.core?.[index]?.media} {...{control }} inputDir={inputDir} required />}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="2" title="Title" instructions='In the field below please enter the title that will be displayed with this slide.' />
                      <Controller 
                        control={control}
                        name={`content.core.${index}.title`}
                        render={({ field }) => (
                          <>
                            <TextField 
                              {...field}
                              className="mt-16 mb-16"
                              label="Text"
                              placeholder="Slide Text"
                              error={!!errors.content?.core?.[index]?.title}
                              helperText={errors.content?.core?.[index]?.title?.message}
                              fullWidth
                              required
                            />
                          </>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="3" title="Description" instructions='In the field below please enter the content that will be displayed with this slide.' />

                      <Controller
                        control={control}
                        name={`content.core.${index}.description`}
                        render={({ field }) => <WYSIWYGEditor {...field} error={errors.content?.core?.[index]?.description} />}
                        required
                      />
                    </Box>
                    <Box className="flex items-center mt-32 p-0 border-t" sx={{backgroundColor: "background.default"}}>
                    {index !== 0 &&
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index - 1)}
                        >
                          <BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Up
                          </span>
                        </Button>
                      }
                      {index !== fields.length - 1 &&
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index + 1)}
                        >
                          <BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Down
                          </span>
                        </Button>
                      }
                      <Button
                        className="group flex items-center mt-2 py-2 px-4 rounded cursor-pointer ml-auto"
                        onClick={() => remove(index)}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:minus-circle</BASESvgIcon>
                        <span className="ml-8 font-medium text-secondary group-hover:underline">
                          Remove Slide
                        </span>
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          )                    
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-2 -ml-4 py-2 px-4 rounded cursor-pointer"
          onClick={() => append({ 
            id: BASEUtils.generateGUID(),
            review: ReviewData,
            media: MediaObject,
            title: "",
            description: "",
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add slide
          </span>
        </Button>
      </Box>
    </>
  )
}